import { useTranslation } from 'react-i18next';

import {
  ColumnConfig,
  TableColumnHeader,
} from '@/common/components/table/TableColumnHeader';
import { TableHeader, TableRow } from '@/common/components/ui/table';

import { BillingFilters } from '../api/FetchBillingList';
import { BillingSortField, SortOrder } from '../types/SortTypes';

interface BillingHeaderProps {
  sortBy: BillingSortField;
  sortOrder: SortOrder;
  filters: BillingFilters;
  onSort: (field: BillingSortField) => void;
  onFilterChange: (field: string, value: string | string[]) => void;
}

export const BillingHeader = ({
  sortBy,
  sortOrder,
  filters,
  onSort,
  onFilterChange,
}: BillingHeaderProps) => {
  const { t } = useTranslation();

  const columns: ColumnConfig<BillingSortField>[] = [
    {
      field: null,
      label: '',
      sortable: false,
      filterable: false,
      width: 'w-[50px]',
    },
    {
      field: 'status',
      label: t('billing.status'),
      sortable: true,
      filterable: true,
      filterType: 'multiselect',
      options: [
        {
          value: 'PENDING_VALIDATION',
          label: t('PENDING_VALIDATION'),
        },
        {
          value: 'IN_PROGRESS',
          label: t('IN_PROGRESS'),
        },
        {
          value: 'NOT_COMPLETED',
          label: t('NOT_COMPLETED'),
        },
        {
          value: 'COMPLETED',
          label: t('COMPLETED'),
        },
      ],
      filterKey: 'statuses',
      filterPlaceholder: t('billing.filter.status'),
      width: 'min-w-[120px]',
    },
    {
      field: 'createdAt',
      label: t('billing.deversement'),
      sortable: true,
      filterable: false,
      width: 'min-w-[120px]',
    },
    {
      field: null,
      label: t('billing.notes'),
      sortable: false,
      filterable: false,
      width: 'min-w-85',
    },
    {
      field: 'searchCode',
      label: t('billing.searchCode'),
      sortable: true,
      filterable: true,
      filterType: 'text',
      filterKey: 'searchCode',
      filterPlaceholder: t('billing.filter.searchCode'),
      width: 'min-w-[180px]',
    },
    {
      field: 'insurer',
      label: t('billing.insurer'),
      sortable: true,
      filterable: true,
      filterType: 'text',
      filterKey: 'insurer',
      filterPlaceholder: t('billing.filter.insurer'),
      width: 'min-w-[130px]',
    },
    {
      field: 'activityCode',
      label: t('billing.activityCode'),
      sortable: true,
      filterable: true,
      filterType: 'text',
      filterKey: 'activityCode',
      filterPlaceholder: t('billing.filter.activityCode'),
      width: 'min-w-[160px]',
    },
    {
      field: 'policyNumber',
      label: t('billing.policyNumber'),
      sortable: true,
      filterable: true,
      filterType: 'text',
      filterKey: 'policyNumber',
      filterPlaceholder: t('billing.filter.policyNumber'),
      width: 'min-w-[140px]',
    },
    {
      field: 'effectiveDate',
      label: t('billing.effectiveDate'),
      sortable: true,
      filterable: false,
      width: 'min-w-[120px]',
    },
    {
      field: 'expiryDate',
      label: t('billing.expiryDate'),
      sortable: true,
      filterable: false,
      width: 'min-w-[130px]',
    },
    {
      field: 'gc1',
      label: t('billing.gc1'),
      sortable: true,
      filterable: true,
      filterType: 'text',
      filterKey: 'gc1',
      filterPlaceholder: t('billing.filter.gc1'),
      width: 'min-w-[120px]',
    },
    {
      field: 'isAgencyBill',
      label: t('billing.agencyOrDirectBilling'),
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterKey: 'isAgencyBill',
      options: [
        { value: '', label: t('billing.filter.all') },
        { value: 'true', label: t('billing.agency') },
        { value: 'false', label: t('billing.direct') },
      ],
      width: 'min-w-[120px]',
    },
    {
      field: 'technicalSupport',
      label: t('billing.technicalSupport'),
      sortable: false,
      filterable: true,
      width: 'min-w-[140px]',
      filterType: 'text',
      filterKey: 'technicalSupport',
      filterPlaceholder: t('billing.filter.technicalSupport'),
    },
    {
      field: 'completedDate',
      label: t('billing.completedDate'),
      sortable: true,
      filterable: false,
      width: 'min-w-[120px]',
    },
    {
      field: 'assignedUserId',
      label: t('billing.associatedBy'),
      sortable: true,
      filterable: true,
      filterType: 'text',
      filterKey: 'assignedUserId',
      filterPlaceholder: t('billing.filter.associatedBy'),
      width: 'min-w-[140px]',
    },
    {
      field: 'service',
      label: t('billing.service'),
      sortable: false,
      filterable: true,
      filterType: 'text',
      filterKey: 'service',
      filterPlaceholder: t('billing.filter.service'),
      width: 'min-w-[100px]',
    },
    {
      field: 'policyType',
      label: t('billing.policyType'),
      sortable: true,
      filterable: true,
      filterType: 'text',
      filterKey: 'policyType',
      filterPlaceholder: t('billing.filter.policyType'),
      width: 'min-w-[140px]',
    },
  ];

  return (
    <TableHeader className="sticky top-0 z-10 bg-background">
      <TableRow>
        {columns.map((column) => (
          <TableColumnHeader
            key={column.label}
            column={column}
            sortBy={sortBy}
            sortOrder={sortOrder}
            filters={filters}
            onSort={onSort}
            onFilterChange={onFilterChange}
          />
        ))}
      </TableRow>
    </TableHeader>
  );
};
